import BoringAvatar from 'boring-avatars'
import { User } from 'lucide-react'

import { categoricalColors } from '@/features/dashboard/chart-colors'
import { cn } from '@/shared/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'

/** The application's chart palette, so generated avatars match the rest of it. */
const COLORS = categoricalColors(5)

/**
 * An avatar drawn from a seed: the same seed always gives the same picture.
 * `beam` reads as a person, `marble` as software.
 */
export function GeneratedAvatar({
  seed,
  variant,
  className,
}: {
  seed: string
  variant: 'beam' | 'marble'
  className?: string
}) {
  return (
    <BoringAvatar
      name={seed}
      variant={variant}
      colors={COLORS}
      size="100%"
      square
      aria-hidden
      className={cn('shrink-0', className)}
    />
  )
}

/**
 * The identity provider's avatar for an account, or one generated from `seed`.
 *
 * The URL is the upstream provider's and may have stopped resolving, so the
 * fallback covers a broken image as well as an absent one. A person icon stands
 * in when there is no seed either. Decorative: every caller puts the name
 * beside it.
 */
export function UserAvatar({
  src,
  seed,
  className,
}: {
  src: string
  /** Stable per account; the email address. */
  seed: string
  className?: string
}) {
  return (
    <Avatar className={className}>
      {src !== '' && <AvatarImage src={src} alt="" referrerPolicy="no-referrer" />}
      <AvatarFallback>
        {seed === '' ? (
          <User className="size-4" />
        ) : (
          <GeneratedAvatar seed={seed} variant="beam" />
        )}
      </AvatarFallback>
    </Avatar>
  )
}
